/** Number display and parsing helpers shared by NumberField, Slider and ColorField. */

/** Rounds to `precision` decimals and trims trailing zeros ("1.50" -> "1.5"); -0 shows as 0. */
export function formatFieldNumber(v: number, precision: number): string {
  if (!Number.isFinite(v)) return String(v);
  const p = Math.max(0, Math.min(10, Math.round(precision)));
  let s = v.toFixed(p);
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s === '-0' ? '0' : s;
}

const PLAIN_NUMBER_RE = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;

/**
 * Plain decimal number, optionally followed by the field's own unit ("12", "-3.5", "1e3", "45deg" when the unit
 * is "deg"). Anything else (expressions, other units) returns null and goes to the expression language.
 */
export function parsePlainNumber(src: string, unit?: string): number | null {
  let s = src.trim();
  if (unit !== undefined && unit !== '' && s.toLowerCase().endsWith(unit.toLowerCase())) {
    s = s.slice(0, s.length - unit.length).trim();
  }
  if (!PLAIN_NUMBER_RE.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function clamp(v: number, min: number | undefined, max: number | undefined): number {
  let out = v;
  if (min !== undefined && out < min) out = min;
  if (max !== undefined && out > max) out = max;
  return out;
}

/** Removes binary noise from a sum of steps (0.1 + 0.2 -> 0.3) by rounding to the step's decimals + 6. */
export function roundToStep(v: number, step: number): number {
  const decimals = Math.min(12, decimalsOf(step) + 6);
  return Number(v.toFixed(decimals));
}

export function decimalsOf(x: number): number {
  if (!Number.isFinite(x)) return 0;
  const s = String(x);
  const e = /e-(\d+)$/.exec(s);
  if (e !== null) return Number(e[1]);
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : s.length - dot - 1;
}
